import { computed, observable, makeObservable, reaction, runInAction } from 'mobx';
import { Processo } from '../modelo';
import { DadosProcessoBTV } from '../modelo/DadosProcessoBTV';
import { MINUTA_DEVOLVIDA } from '../modelo/SituacaoMinuta';
import GabineteStore from './GabineteStore';
import LayoutStore from './LayoutStore';

const SEM_OJC_CADASTRADO = 'Sem OJC cadastrado';

interface IProcessosPorControle {
    todos: Processo[];
    noFluxo: Processo[];
    porOjc: Map<string, Processo[]>;
    noFluxoPorOjc: Map<string, Processo[]>;
}

class ProcessoStore {
    carregandoProcessosNoEscaninho = false;
    carregouProcessosNoEscaninho = false;
    carregandoPartesDeProcesso = false;
    carregouPartes = false;
    carregandoHistoricoMinutasProcesso = false;
    carregouHistoricoMinutas = false;
    carregouDocPeticoes = false;
    // @observable carregandoDocPeticoes = false;
    carregandoPeticoesPendentes = false;
    carregandoPeticoesPendentesEscaninho = false;
    carregouPeticoesPendentes = false;
    carregandoQTDPeticoesPendentes = false;
    carregouQtdPeticoes = false;
    carregandoRetornos = false;
    carregouRetornos = false;
    carregandoImpedimentosDeProcesso = false;
    carregouImpedimentos = false;
    carregandoAssuntos = false;
    carregouAssuntos = false;
    carregandoLiberacao = false;
    carregouLiberacao = false;
    carregandoIndicadores = false;
    carregouIndicadores = false;
    carregandoResultadosJulgamento = false;
    carregouResultadosJulgamento = false;
    carregandoSituacaoPauta = false;
    carregouSituacaoPauta = false;
    carregandoTipoJulgamento = false;
    carregandoMovimentosDeMinuta = false;
    carregandoMovimentosPje = false;
    carregouMovimentosPje = false;
    carregandoCorreJuntos = false;
    carregouCorreJuntos = false;
    carregandoProcessosAssociados = false;
    carregouProcessosAssociados = false;
    ordenar = "";
    campo = "";
    _processos: Processo[] = [];
    _processosForaDoGE: Processo[] = [];
    dadosBTV: Map<string, DadosProcessoBTV>;
    mostraIconeCustosLegis: boolean = true;
    carregandoResumoDocumentos: boolean = false;
    isMinutaAguia: boolean = false;
    isPossivelCriarMinutaAguia: boolean = false;

    constructor() {
        makeObservable(this, {
            carregandoProcessosNoEscaninho: observable,
            carregouProcessosNoEscaninho: observable,
            carregandoPartesDeProcesso: observable,
            carregouPartes: observable,
            carregandoHistoricoMinutasProcesso: observable,
            carregouHistoricoMinutas: observable,
            carregouDocPeticoes: observable,
            carregandoPeticoesPendentes: observable,
            carregandoPeticoesPendentesEscaninho: observable,
            carregouPeticoesPendentes: observable,
            carregandoQTDPeticoesPendentes: observable,
            carregouQtdPeticoes: observable,
            carregandoRetornos: observable,
            carregouRetornos: observable,
            carregandoImpedimentosDeProcesso: observable,
            carregouImpedimentos: observable,
            carregandoAssuntos: observable,
            carregouAssuntos: observable,
            carregandoLiberacao: observable,
            carregouLiberacao: observable,
            carregandoIndicadores: observable,
            carregouIndicadores: observable,
            carregandoResultadosJulgamento: observable,
            carregouResultadosJulgamento: observable,
            carregandoSituacaoPauta: observable,
            carregouSituacaoPauta: observable,
            carregandoTipoJulgamento: observable,
            carregandoMovimentosDeMinuta: observable,
            carregandoMovimentosPje: observable,
            carregouMovimentosPje: observable,
            carregandoCorreJuntos: observable,
            carregouCorreJuntos: observable,
            carregandoProcessosAssociados: observable,
            carregouProcessosAssociados: observable,
            ordenar: observable,
            campo: observable,
            _processos: observable,
            _processosForaDoGE: observable,
            dadosBTV: observable,
            mostraIconeCustosLegis: observable,
            carregandoResumoDocumentos: observable,
            isMinutaAguia: observable,
            isPossivelCriarMinutaAguia: observable,
            processosAgrupados: computed({ keepAlive: true }),
            processosPorControle: computed,
            indiceProcessosPorControle: computed,
            ojcsDisponiveis: computed,
            processosSelecionados: computed
        });

        // Ao alternar tema claro/escuro, o fundo dos processos já carregados
        // fica "preso" na cor do tema anterior (Processo.background é um valor
        // animado, não reativo). Aqui reaplicamos a cor padrão do novo tema.
        reaction(
            () => LayoutStore.isTemaDark,
            () => {
                runInAction(() => {
                    [...this._processos, ...this._processosForaDoGE].forEach(processo => {
                        processo.background = processo.backgroundPadrao?.length > 0
                            ? processo.backgroundPadrao
                            : LayoutStore.backgroundProcessoPadrao;
                    });
                });
            }
        );
    }

    get processosAgrupados(): Processo[] {

        let processos = this._processos.slice();
        processos = this.ordenarProcessos(processos);

        return processos;
    }

    get processosPorControle(): Map<number, Processo[]> {
        const map = new Map<number, Processo[]>();

        this._processos.forEach(proc => {
            const numero = proc.controleGabinete?.numero;
            if (numero === undefined || numero === null) return;

            if (!map.has(numero)) {
                map.set(numero, []);
            }

            map.get(numero)!.push(proc);
        });

        return map;
    }

    get indiceProcessosPorControle(): Map<number, IProcessosPorControle> {
        const indice = new Map<number, IProcessosPorControle>();

        this._processos.forEach(processo => {
            const numeroControle = processo.controleGabinete?.numero;
            if (numeroControle === undefined || numeroControle === null) return;

            let agrupamento = indice.get(numeroControle);
            if (!agrupamento) {
                agrupamento = {
                    todos: [],
                    noFluxo: [],
                    porOjc: new Map<string, Processo[]>(),
                    noFluxoPorOjc: new Map<string, Processo[]>()
                };
                indice.set(numeroControle, agrupamento);
            }

            const siglaOjc = processo.orgaoJulgadorColegiado?.sigla?.trim() || SEM_OJC_CADASTRADO;
            agrupamento.todos.push(processo);
            this.adicionarProcessoAoIndiceOjc(agrupamento.porOjc, siglaOjc, processo);

            if (processo.noFluxoCerto) {
                agrupamento.noFluxo.push(processo);
                this.adicionarProcessoAoIndiceOjc(agrupamento.noFluxoPorOjc, siglaOjc, processo);
            }
        });

        return indice;
    }

    get ojcsDisponiveis(): string[] {
        const ojcs = new Set<string>();

        this._processos.forEach(processo => {
            ojcs.add(processo.orgaoJulgadorColegiado?.sigla?.trim() || SEM_OJC_CADASTRADO);
        });

        return Array.from(ojcs).sort((a, b) => {
            if (a === SEM_OJC_CADASTRADO) return 1;
            if (b === SEM_OJC_CADASTRADO) return -1;
            return a.localeCompare(b, 'pt-BR', { sensitivity: 'base' });
        });
    }

    processosDoControle(
        numeroControle: number,
        apenasNoFluxo = false,
        filtroOjc?: string | string[] | null
    ): Processo[] {
        const agrupamento = this.indiceProcessosPorControle.get(numeroControle);
        if (!agrupamento) return [];

        if (!filtroOjc || (Array.isArray(filtroOjc) && filtroOjc.length === 0)) {
            return apenasNoFluxo ? agrupamento.noFluxo : agrupamento.todos;
        }

        const indiceOjc = apenasNoFluxo ? agrupamento.noFluxoPorOjc : agrupamento.porOjc;
        const ojcs = Array.isArray(filtroOjc) ? filtroOjc : [filtroOjc];

        return ojcs.flatMap(ojc => indiceOjc.get(ojc.trim()) || []);
    }

    private adicionarProcessoAoIndiceOjc(
        indice: Map<string, Processo[]>,
        siglaOjc: string,
        processo: Processo
    ) {
        const processos = indice.get(siglaOjc);
        if (processos) {
            processos.push(processo);
        } else {
            indice.set(siglaOjc, [processo]);
        }
    }

    sinalOrdenacao = 1;
    ordenarProcessos = (processos: Processo[]) => {
        if (this.ordenar) {

            this.sinalOrdenacao = this.ordenar === "A - Z" ? 1 : -1;

            let funcaoDeOdenacao;
            switch (this.campo) {
                case 'numeroCpfResponsavel':
                    funcaoDeOdenacao = this.ordenarPorNomeResponsavel;
                    break;
                case 'controleGabinete.descricao':
                    funcaoDeOdenacao = this.ordenarPorControleGabinete;
                    break;
                case 'OJC':
                    funcaoDeOdenacao = this.ordenarPorOJC;
                    break;
                case 'dataUltimaModificacao':
                    funcaoDeOdenacao = this.ordenarPorDataUltimaModificacao;
                    break;
                case 'pauta.data':
                    funcaoDeOdenacao = this.ordenarPorDataPauta;
                    break;
                case 'pauta.idPauta':
                    funcaoDeOdenacao = this.ordenaPorSessao;
                    break;
                case 'tipoPet':
                    funcaoDeOdenacao = this.ordenarPorTipoPeticao;
                    break;
                case 'dataPet':
                    funcaoDeOdenacao = this.ordenarPorDataPeticao;
                    break;
                default:
                    funcaoDeOdenacao = this.ordenarPorCampo;
                    break;
            }
            const retorno = processos.sort(funcaoDeOdenacao);
            return retorno;
        } else {
            return processos;
        }
    }

    ordenarPorNomeResponsavel = (a, b) => { // TODO
        const nomeServidorA = GabineteStore.recuperarNomeServidor(a[this.campo]) || 'Sem Responsável';
        const nomeServidorB = GabineteStore.recuperarNomeServidor(b[this.campo]) || 'Sem Responsável';
        return nomeServidorA > nomeServidorB ? this.sinalOrdenacao : -1 * this.sinalOrdenacao;
    }

    ordenarPorControleGabinete = (a, b) => {
        return a.controleGabinete.descricao.toLowerCase() > (b.controleGabinete.descricao.toLowerCase()) ? 1 * this.sinalOrdenacao : -1 * this.sinalOrdenacao
    }
    ordenarPorOJC = (a, b) => {
        if (b.orgaoJulgadorColegiado?.sigla < a.orgaoJulgadorColegiado?.sigla) {
            return 1 * this.sinalOrdenacao;
        }
        if (b.orgaoJulgadorColegiado?.sigla > a.orgaoJulgadorColegiado?.sigla) {
            return -1 * this.sinalOrdenacao;
        }
        return 0;
    }
    ordenarPorDataUltimaModificacao = (a, b) => {
        const dataA = new Date(a.dataUltimaModificacao);
        const dataB = new Date(b.dataUltimaModificacao);

        if (dataA < dataB) {
            return -1 * this.sinalOrdenacao;
        }
        if (dataA > dataB) {
            return 1 * this.sinalOrdenacao;
        }
        return 0;
    }
    ordenarPorDataPauta = (a, b) => {
        if (!a.pauta && !b.pauta) return 0;
        if (!a.pauta) return 1 * this.sinalOrdenacao;
        if (!b.pauta) return -1 * this.sinalOrdenacao;

        const pautaA = new Date(a.pauta.dataSessao);
        const pautaB = new Date(b.pauta.dataSessao);

        if (isNaN(pautaA.getTime())) return 1 * this.sinalOrdenacao;
        if (isNaN(pautaB.getTime())) return -1 * this.sinalOrdenacao;

        if (pautaA < pautaB) return -1 * this.sinalOrdenacao;
        if (pautaA > pautaB) return 1 * this.sinalOrdenacao;

        return 0;
    };
    ordenaPorSessao = (a, b) => {
        if (!a.pauta && !b.pauta) return 0;
        if (!a.pauta) return 1 * this.sinalOrdenacao;
        if (!b.pauta) return -1 * this.sinalOrdenacao;

        const idA = a.pauta.idPauta ? parseInt(a.pauta.idPauta.replace(/\D/g, ""), 10) : NaN;
        const idB = b.pauta.idPauta ? parseInt(b.pauta.idPauta.replace(/\D/g, ""), 10) : NaN;

        if (isNaN(idA)) return 1 * this.sinalOrdenacao;
        if (isNaN(idB)) return -1 * this.sinalOrdenacao;

        if (idA < idB) return -1 * this.sinalOrdenacao;
        if (idA > idB) return 1 * this.sinalOrdenacao;

        return 0;
    };
    ordenarPorTipoPeticao = (a, b) => {
        let idPeticaoA;
        let idPeticaoB;
        a.peticoesPendentes?.map((idPeticao) => {
            return (idPeticaoA = idPeticao?.idUnico)
        });
        b.peticoesPendentes?.map((numeroPeticao) => {
            return (idPeticaoB = numeroPeticao?.idUnico)
        });
        if (idPeticaoB < idPeticaoA) {
            return 1 * this.sinalOrdenacao;
        } if (idPeticaoB > idPeticaoA) {
            return -1 * this.sinalOrdenacao;
        }
        return 0;
    }
    ordenarPorDataPeticao = (a, b) => {
        if (!a.peticoesPendentes || a.peticoesPendentes.length == 0) {
            return -1 * this.sinalOrdenacao;
        }
        if (!b.peticoesPendentes || b.peticoesPendentes.length == 0) {
            return 1 * this.sinalOrdenacao;
        }
        let dataPeticaoA;
        let dataPeticaoB;
        a.peticoesPendentes.forEach((dataPeticao) => {
            const criadoEm = new Date(dataPeticao.criadoEm);
            if (!dataPeticaoA) {
                dataPeticaoA = criadoEm;
                return;
            }
            if (this.sinalOrdenacao > 0 && dataPeticaoA <= criadoEm) {
                dataPeticaoA = criadoEm;
            } else if (this.sinalOrdenacao < 0 && dataPeticaoA > criadoEm) {
                dataPeticaoA = criadoEm;
            }
        });
        b.peticoesPendentes.forEach((dataPeticao) => {
            const criadoEm = new Date(dataPeticao.criadoEm);
            if (!dataPeticaoB) {
                dataPeticaoB = criadoEm;
                return;
            }
            if (this.sinalOrdenacao > 0 && dataPeticaoB <= criadoEm) {
                dataPeticaoB = criadoEm;
            } else if (this.sinalOrdenacao < 0 && dataPeticaoB > criadoEm) {
                dataPeticaoB = criadoEm;
            }
        });
        if (dataPeticaoA < dataPeticaoB) {
            return -1 * this.sinalOrdenacao;
        }
        if (dataPeticaoA > dataPeticaoB) {
            return 1 * this.sinalOrdenacao;
        }
        return 0;
    }
    ordenarPorCampo = (a, b) => {
        if (b[this.campo] < a[this.campo]) {
            return 1 * this.sinalOrdenacao;
        }
        if (b[this.campo] > a[this.campo]) {
            return -1 * this.sinalOrdenacao;
        }
        return 0;
    }

    get processosSelecionados(): Processo[] {
        return this._processos.filter(processo => processo.selecionado && processo.mostrar);
    }

    get processosSelecionadosTirandoMinutaDevolvidas(): Processo[] {
        return this.processosSelecionados.filter(processo => {
            const minutasDevolvidas = processo.minutas.filter(min =>
                min.situacaoMinuta?.codigo === (new MINUTA_DEVOLVIDA()).codigo
            );

            return processo.selecionado && processo.minutas.length > minutasDevolvidas.length;
        });
    }

    get processosSelecionadosApenasComMinutaDevolvidasSelecionadas(): Processo[] {
        return this.processosSelecionados.filter(processo => {
            const minutasDevolvidas = processo.minutas.filter(min =>
                min.situacaoMinuta?.codigo === (new MINUTA_DEVOLVIDA()).codigo
            );

            return processo.selecionado && (minutasDevolvidas.length > 0);
        });
    }

    limpar = () => {
        this._processos = [];
        this.carregandoProcessosNoEscaninho = false;
        this.carregouProcessosNoEscaninho = false;
        this.carregandoPartesDeProcesso = false;
        this.carregouPartes = false;
        this.carregandoHistoricoMinutasProcesso = false;
        this.carregouHistoricoMinutas = false;
        this.carregouDocPeticoes = false;
        // this.carregandoDocPeticoes = false;
        this.carregandoPeticoesPendentes = false;
        this.carregandoPeticoesPendentesEscaninho = false;
        this.carregouPeticoesPendentes = false;
        this.carregandoQTDPeticoesPendentes = false;
        this.carregouQtdPeticoes = false;
        this.carregandoRetornos = false;
        this.carregouRetornos = false;
        this.carregandoImpedimentosDeProcesso = false;
        this.carregouImpedimentos = false;
        this.carregandoAssuntos = false;
        this.carregouAssuntos = false;
        this.carregandoLiberacao = false;
        this.carregouLiberacao = false;
        this.carregandoIndicadores = false;
        this.carregouIndicadores = false;
        this.carregandoResultadosJulgamento = false;
        this.carregouResultadosJulgamento = false;
        this.carregandoSituacaoPauta = false;
        this.carregouSituacaoPauta = false;
        this.carregandoTipoJulgamento = false;
        this.carregandoMovimentosPje = false;
        this.carregouMovimentosPje = false;
        this.ordenar = "";
        this.campo = "";
        this._processosForaDoGE = [];
        this.carregandoCorreJuntos = false;
        this.carregouCorreJuntos = false;
        this.carregandoProcessosAssociados = false;
        this.carregouProcessosAssociados = false;
    }
}

export default new ProcessoStore();
